import type { CostCategory, Money, TerminalTier } from '../interfaces/costs.interface';

type Lang = 'ro' | 'en';

export const CATEGORY_LABEL: Record<CostCategory, { ro: string; en: string }> = {
  standard: { ro: 'Standard (DC / HC)', en: 'Standard (DC / HC)' },
  imo: { ro: 'IMO / marfă periculoasă', en: 'IMO / dangerous goods' },
  reefer: { ro: 'Reefer (frigorific)', en: 'Reefer' },
  special: { ro: 'Echipament special (OT / FR)', en: 'Special equipment (OT / FR)' },
  oog_small: { ro: 'Special, depășire gabarit ≤ 1,5 m', en: 'Special, out of gauge ≤ 1.5 m' },
  oog_large: { ro: 'Special, depășire gabarit > 1,5 m', en: 'Special, out of gauge > 1.5 m' },
};

export const categoryLabel = (c: CostCategory, lang: Lang) => CATEGORY_LABEL[c]?.[lang] ?? c;

export const fmtMoney = (amount: number, lang: Lang, digits = 0) =>
  new Intl.NumberFormat(lang === 'ro' ? 'ro-RO' : 'en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);

export const fmtPrice = (m: Money | null | undefined, lang: Lang) =>
  m ? `${fmtMoney(m.amount, lang)} ${m.currency}` : '—';

/** Totaluri pe monedă (navlul e de regulă în USD, localele în EUR — nu se convertesc). */
export function sumByCurrency(items: (Money | null | undefined)[]): Money[] {
  const map = new Map<string, number>();
  for (const m of items) {
    if (!m) continue;
    const code = (m.currency || '').toUpperCase() || 'EUR';
    map.set(code, (map.get(code) || 0) + m.amount);
  }
  return [...map.entries()].map(([currency, amount]) => ({ currency, amount })).sort((a, b) => b.amount - a.amount);
}

export const fmtTotals = (list: Money[], lang: Lang) =>
  list.length ? list.map((m) => `${fmtMoney(m.amount, lang)} ${m.currency}`).join(' + ') : '—';

/** „Zilele 15–19", „Din ziua 25" — ziua 1 = ziua descărcării. */
export function tierRange(t: { fromDay: number; toDay: number | null }, lang: Lang): string {
  if (t.toDay == null) return lang === 'ro' ? `Din ziua ${t.fromDay}` : `From day ${t.fromDay}`;
  if (t.toDay === t.fromDay) return lang === 'ro' ? `Ziua ${t.fromDay}` : `Day ${t.fromDay}`;
  return lang === 'ro' ? `Zilele ${t.fromDay}–${t.toDay}` : `Days ${t.fromDay}–${t.toDay}`;
}

export const freeDaysLabel = (n: number, lang: Lang) => {
  if (n <= 0) return lang === 'ro' ? 'Fără zile libere' : 'No free days';
  if (lang === 'ro') return n === 1 ? '1 zi liberă' : `${n} zile libere`;
  return n === 1 ? '1 free day' : `${n} free days`;
};

export type Size = '20' | '40' | '45';

export const tierPrice = (t: TerminalTier, size: Size) => (size === '20' ? t.price20 : size === '45' ? t.price45 : t.price40);

/**
 * Suma pentru `days` zile de la descărcare (ziua descărcării = 1), peste zilele libere.
 * Aceeași regulă ca facturarea (backend: shared/utils/local-charges.util.ts → computeCharge).
 */
export function chargeForDays(days: number, freeDays: number, tiers: TerminalTier[], size: Size) {
  const sorted = [...tiers].sort((a, b) => a.fromDay - b.fromDay);
  const priceForDay = (d: number) => {
    const t = sorted.find((x) => d >= x.fromDay && d <= (x.toDay ?? Number.POSITIVE_INFINITY));
    if (t) return tierPrice(t, size);
    if (!sorted.length) return 0;
    return tierPrice(d < sorted[0].fromDay ? sorted[0] : sorted[sorted.length - 1], size);
  };
  let amount = 0;
  let billable = 0;
  for (let d = freeDays + 1; d <= days; d += 1) {
    billable += 1;
    amount += priceForDay(d);
  }
  return { amount: Math.round(amount * 100) / 100, billable };
}

/** Eticheta perioadei de valabilitate. */
export function validityLabel(from: string | null, to: string | null, lang: Lang, fmt: (iso: string) => string) {
  if (from && to) return lang === 'ro' ? `Valabil ${fmt(from)} – ${fmt(to)}` : `Valid ${fmt(from)} – ${fmt(to)}`;
  if (from) return lang === 'ro' ? `Valabil din ${fmt(from)}` : `Valid from ${fmt(from)}`;
  if (to) return lang === 'ro' ? `Valabil până la ${fmt(to)}` : `Valid until ${fmt(to)}`;
  return lang === 'ro' ? 'Tarif în vigoare' : 'Current tariff';
}
